const RECURRENCES = ["none", "daily", "weekdays", "weekly", "monthly"];

/** Strip optional ```json ... ``` wrapper from pasted text. */
export function stripJsonCodeFence(text) {
  let s = String(text || "").trim();
  if (!s) return s;
  const whole = /^```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```\s*$/i;
  const mWhole = s.match(whole);
  if (mWhole) return mWhole[1].trim();
  const inner = /```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```/i;
  const mInner = s.match(inner);
  if (mInner) return mInner[1].trim();
  return s;
}

function normalizeRecurrence(raw) {
  const value = String(raw || "none").trim();
  return RECURRENCES.includes(value) ? value : "none";
}

function normalizeDueOffset(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.trunc(n);
}

function spawnFromRow(raw) {
  if (raw?.spawn_checklist === true) return true;
  const action = raw?.reference_action;
  return action && typeof action === "object" && action.action === "spawn_checklist";
}

function checklistTypeIdFromRow(raw) {
  const fromRoot = raw?.checklist_type_id;
  const fromAction = raw?.reference_action?.checklist_type_id;
  const id = fromRoot ?? fromAction;
  if (id == null || id === "") return "";
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? String(n) : "";
}

export function normalizeProjectTaskTemplateItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  const title = String(raw.title || raw.name || "").trim().slice(0, 100);
  if (!title) return null;

  const shared = Boolean(raw.shared);
  return {
    title,
    description: String(raw.description || "").trim(),
    due_offset_days: normalizeDueOffset(raw.due_offset_days),
    shared,
    completion_mode: shared ? "any" : raw.completion_mode === "any" ? "any" : "single",
    recurrence: normalizeRecurrence(raw.recurrence),
    spawn_checklist: spawnFromRow(raw),
    checklist_type_id: checklistTypeIdFromRow(raw),
    checklist_type_title: String(raw.checklist_type_title || raw.checklist_type || "").trim(),
  };
}

/**
 * Parse pasted string into template-set shape.
 * Accepts: raw items array, { items }, or full set shape.
 */
export function parseImportedProjectTaskTemplateJson(text) {
  const trimmed = stripJsonCodeFence(text);
  if (!trimmed) {
    const err = new Error("empty");
    err.code = "empty";
    throw err;
  }

  let data;
  try {
    data = JSON.parse(trimmed);
  } catch {
    const err = new Error("invalid_json");
    err.code = "invalid_json";
    throw err;
  }

  let arr;
  let title;
  let description;
  let category;
  let active;

  if (Array.isArray(data)) {
    arr = data;
  } else if (data && Array.isArray(data.items)) {
    arr = data.items;
    title = data.title;
    description = data.description;
    category = data.category;
    active = data.active;
  } else {
    const err = new Error("no_items_array");
    err.code = "no_items_array";
    throw err;
  }

  const items = arr.map((row) => normalizeProjectTaskTemplateItem(row)).filter(Boolean);

  if (items.length === 0) {
    const err = new Error("no_valid_items");
    err.code = "no_valid_items";
    throw err;
  }

  return {
    items,
    title: title != null ? String(title).trim() : undefined,
    description: description != null ? String(description).trim() : undefined,
    category: category != null ? String(category).trim() : undefined,
    active: active == null ? undefined : Boolean(active),
  };
}

export function serializeProjectTaskTemplateForExport(values = {}) {
  const payload = {
    title: values.title || "",
    description: values.description || "",
    category: values.category || "",
    active: values.active !== false,
    items: (values.items || []).map((row) => {
      const item = {
        title: row.title || "",
        description: row.description || "",
        due_offset_days: Number(row.due_offset_days) || 0,
        shared: Boolean(row.shared),
        completion_mode: row.shared ? "any" : row.completion_mode || "single",
        recurrence: normalizeRecurrence(row.recurrence),
        spawn_checklist: Boolean(row.spawn_checklist),
      };
      if (row.spawn_checklist && row.checklist_type_id) {
        item.checklist_type_id = Number(row.checklist_type_id);
      }
      return item;
    }),
  };
  return JSON.stringify(payload, null, 2);
}

/** Match AI checklist_type_title against loaded types (id or title). */
export function resolveChecklistTypeId(item, checklistTypes = []) {
  if (item?.checklist_type_id) {
    const byId = checklistTypes.find(
      (ct) => String(ct.id) === String(item.checklist_type_id)
    );
    if (byId) return String(byId.id);
  }
  const needle = String(item?.checklist_type_title || "").trim().toLowerCase();
  if (!needle) return item?.checklist_type_id || "";
  const byTitle = checklistTypes.find((ct) => {
    const title = String(ct.displayTitle || ct.title || "").trim().toLowerCase();
    return title && title === needle;
  });
  return byTitle ? String(byTitle.id) : item?.checklist_type_id || "";
}

export const SAMPLE_PROJECT_TASK_TEMPLATE_JSON = `{
  "title": "Nytt prosjekt",
  "description": "Standard oppgaver ved oppstart av nytt prosjekt",
  "category": "Oppstart",
  "active": true,
  "items": [
    {
      "title": "Kickoff-møte med kunde",
      "description": "Avklar omfang, fremdrift og kontaktpersoner.",
      "due_offset_days": 0,
      "shared": true,
      "completion_mode": "any",
      "recurrence": "none"
    },
    {
      "title": "Følg opp KS",
      "due_offset_days": 7,
      "shared": false,
      "recurrence": "weekly",
      "spawn_checklist": true,
      "checklist_type_title": "Vernerunde"
    },
    {
      "title": "Fakturering iht. plan",
      "due_offset_days": 30,
      "recurrence": "monthly"
    }
  ]
}`;
